import React, { useState, useContext, useRef, useEffect } from "react";
import Swal from "sweetalert2";

import Footer from "../Templete/Footer";
import Header from "../Templete/Header";

import FormService from "../../Services/FormService";
import LoadingContext from "../../Store/loading-context";
import HttpError from "../../utill/HttpError";

const ContactUs = () => {
  const loadingCtx = useContext(LoadingContext);

  const [formData, setFormData] = useState({
    message: "",
    subject: "Contact Us",
  });

  const formRef = useRef();
  const nameRef = useRef();
  const emailRef = useRef();
  const mobileRef = useRef();
  const subjectRef = useRef();

  // Set page title for SEO
  useEffect(() => {
    document.title = "Contact Us | Formax Pay - Secure Payment Solutions";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "Get in touch with Formax Pay for secure payment solutions, partnership inquiries, and customer support. Contact us via phone, email, or visit our Kolkata office."
      );
    }

    // Add structured data for SEO
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "Contact Formax Pay",
      description: "Contact page for Formax Pay payment solutions",
      url: window.location.href,
      contactPoint: [
        {
          "@type": "ContactPoint",
          telephone: "+91-9836114520",
          contactType: "sales",
          areaServed: "IN",
          availableLanguage: ["English", "Hindi"],
        },
        {
          "@type": "ContactPoint",
          telephone: "+91-7890090500",
          contactType: "customer service",
          areaServed: "IN",
          availableLanguage: ["English", "Hindi", "Bengali"],
        },
      ],
    });
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const submitHandler = async (e) => {
    e.preventDefault();

    const name = nameRef.current.value;
    const email = emailRef.current.value;
    const mobile = mobileRef.current.value;
    const subject = formData.subject;

    // Basic validation
    if (!name || !email || !mobile || !formData.message) {
      Swal.fire("Oops!", "Please fill all required fields.", "error");
      return;
    }

    loadingCtx.startLoading();

    try {
      const responseData = await FormService.contactForm(
        name,
        email,
        mobile,
        subject,
        formData.message
      );

      const status = responseData.status;
      const message2 = responseData.message;

      if (status === "SUCCESS") {
        formRef.current.reset();
        setFormData({ message: "", subject: "Contact Us" });
        Swal.fire("Success!", message2, "success");
      } else {
        Swal.fire("Opps!", message2, "error");
      }
    } catch (error) {
      const errMsg = HttpError.getError(error);
      Swal.fire("Opps!", errMsg, "error");
    }

    loadingCtx.stopLoading();
  };

  return (
    <>
      <Header />

      {/* Hero Start */}
      <section className="bg-half-170 bg-light d-table w-100">
        <div className="container">
          <div className="row mt-5 justify-content-center">
            <div className="col-lg-12 text-center">
              <div className="pages-heading">
                <h1 className="title mb-3" style={{ fontSize: "2.5rem" }}>
                  Contact Us
                </h1>
                <p className="text-muted para-desc mx-auto mb-0">
                  Get in touch with Formax Pay for secure payment solutions,
                  partnership inquiries, and customer support.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Shape Start */}
      <div className="position-relative">
        <div className="shape overflow-hidden text-white">
          <svg
            viewBox="0 0 2880 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 48H1437.5H2880V0H2160C1442.5 52 720 0 720 0H0V48Z"
              fill="currentColor"
            />
          </svg>
        </div>
      </div>

      {/* Contact Info Cards */}
      <section className="section">
        <div className="container">
          <div className="row align-items-stretch">
            {/* Customer Support Card */}
            <div className="col-lg-4 col-md-6 mb-4 mb-lg-0">
              <div className="card border-0 text-center features feature-clean shadow h-100">
                <div className="icons text-center mx-auto mt-4">
                  <i className="uil uil-whatsapp text-primary h2 mb-0" />
                </div>
                <div className="content p-4">
                  <h5 className="fw-bold">Customer Support</h5>
                  <p className="text-muted">
                    WhatsApp support for quick assistance with your queries
                  </p>
                  <div className="mt-3">
                    <a
                      href="https://wa.me/917890090500"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary h6 d-block"
                    >
                      +91 78900 90500
                    </a>
                    <a
                      href="mailto:helpdesk@formaxpay.com"
                      className="text-muted d-block mt-2"
                    >
                      helpdesk@formaxpay.com
                    </a>
                    <div className="mt-3">
                      <small className="text-muted">(WhatsApp Only)</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sales & Partnership Card */}
            <div className="col-lg-4 col-md-6 mb-4 mb-lg-0">
              <div className="card border-0 text-center features feature-clean shadow h-100">
                <div className="icons text-center mx-auto mt-4">
                  <i className="uil uil-phone-volume text-success h2 mb-0" />
                </div>
                <div className="content p-4">
                  <h5 className="fw-bold">Sales & Partnership</h5>
                  <p className="text-muted">
                    For business inquiries, sales, and partnership opportunities
                  </p>
                  <div className="mt-3">
                    <a
                      href="tel:+919836114520"
                      className="text-success h6 d-block"
                    >
                      +91 98361 14520
                    </a>
                    <a
                      href="mailto:partnership@formaxpay.com"
                      className="text-muted d-block mt-2"
                    >
                      partnership@formaxpay.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Location Card */}
            <div className="col-lg-4 col-md-6">
              <div className="card border-0 text-center features feature-clean shadow h-100">
                <div className="icons text-center mx-auto mt-4">
                  <i className="uil uil-map-marker text-warning h2 mb-0" />
                </div>
                <div className="content p-4">
                  <h5 className="fw-bold">Our Office</h5>
                  <p className="text-muted">
                    Visit our headquarters in Kolkata
                  </p>
                  <div className="mt-3">
                    <p className="text-muted mb-2">
                      FORMAX IT SOLUTIONS PVT. LTD.
                      <br />
                      Kolkata, West Bengal
                    </p>
                    <a
                      href="https://maps.google.com/?q=FORMAX+IT+SOLUTIONS+PVT+LTD+Kolkata"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-warning d-inline-block mt-2"
                    >
                      View on Google Maps
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Form Section */}
        <div className="container mt-100 mt-60">
          <div className="row align-items-center">
            <div className="col-lg-6 col-md-6 pt-2 pt-sm-0">
              <div className="card shadow rounded border-0">
                <div className="card-body py-5 px-4">
                  <h2 className="card-title mb-4">Send us a Message</h2>
                  <div className="custom-form mt-3">
                    <form ref={formRef} onSubmit={submitHandler} role="form">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label
                              htmlFor="name"
                              className="form-label fw-medium"
                            >
                              Your Name <span className="text-danger">*</span>
                            </label>
                            <div className="form-icon position-relative">
                              <i
                                data-feather="user"
                                className="fea icon-sm icons"
                              />
                              <input
                                ref={nameRef}
                                id="name"
                                name="name"
                                type="text"
                                className="form-control ps-5"
                                placeholder="Enter your name"
                                required
                                aria-required="true"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="mb-3">
                            <label
                              htmlFor="email"
                              className="form-label fw-medium"
                            >
                              Your Email <span className="text-danger">*</span>
                            </label>
                            <div className="form-icon position-relative">
                              <i
                                data-feather="mail"
                                className="fea icon-sm icons"
                              />
                              <input
                                ref={emailRef}
                                id="email"
                                name="email"
                                type="email"
                                className="form-control ps-5"
                                placeholder="Enter your email"
                                required
                                aria-required="true"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="mb-3">
                            <label
                              htmlFor="mobile"
                              className="form-label fw-medium"
                            >
                              Mobile Number{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <div className="form-icon position-relative">
                              <i
                                data-feather="phone"
                                className="fea icon-sm icons"
                              />
                              <input
                                ref={mobileRef}
                                id="mobile"
                                name="mobile"
                                type="tel"
                                className="form-control ps-5"
                                placeholder="Enter your mobile number"
                                required
                                aria-required="true"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="mb-3">
                            <label
                              htmlFor="subject"
                              className="form-label fw-medium"
                            >
                              Subject <span className="text-danger">*</span>
                            </label>
                            <div className="form-icon position-relative">
                              <i
                                data-feather="book"
                                className="fea icon-sm icons"
                              />
                              <select
                                id="subject"
                                name="subject"
                                className="form-control ps-5"
                                value={formData.subject}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    subject: e.target.value,
                                  })
                                }
                                required
                                aria-required="true"
                              >
                                <option value="Contact Us">
                                  General Inquiry
                                </option>
                                <option value="Partnership">
                                  Partnership Inquiry
                                </option>
                                <option value="Sales">Sales Inquiry</option>
                                <option value="Support">
                                  Customer Support
                                </option>
                                <option value="Technical">
                                  Technical Support
                                </option>
                                <option value="Other">Other</option>
                              </select>
                            </div>
                          </div>
                        </div>

                        <div className="col-12">
                          <div className="mb-3">
                            <label
                              htmlFor="message"
                              className="form-label fw-medium"
                            >
                              Your Message{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <div className="form-icon position-relative">
                              <i
                                data-feather="message-circle"
                                className="fea icon-sm icons"
                              />
                              <textarea
                                id="message"
                                name="message"
                                rows={5}
                                className="form-control ps-5"
                                placeholder="Tell us how we can help you..."
                                value={formData.message}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    message: e.target.value,
                                  })
                                }
                                required
                                aria-required="true"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-12">
                          <div className="d-grid">
                            <button
                              type="submit"
                              className="btn btn-primary btn-lg"
                              disabled={loadingCtx.isLoading}
                            >
                              {loadingCtx.isLoading ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2"></span>
                                  Sending Message...
                                </>
                              ) : (
                                <>
                                  <i className="uil uil-message me-2"></i>
                                  Send Message
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-6 col-md-6 mt-4 mt-md-0">
              <div className="card border-0 shadow h-100">
                <div className="card-body p-0">
                  <div className="h-100">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3679.5700719210745!2d88.436413!3d22.7442159!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39f899f2d9615027%3A0x8b7693dab1225dc8!2sFORMAX%20IT%20SOLUTIONS%20PRIVATE%20LIMITED!5e0!3m2!1sen!2sin!4v1768208844410!5m2!1sen!2sin"
                      width="100%"
                      height="500"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Formax IT Solutions Location - Kolkata Office"
                      aria-label="Google Maps location of Formax IT Solutions Private Limited"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Structured Data for Contact Information */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Formax Pay",
            url: "https://formaxpay.com",
            logo: "https://formaxpay.com/logo.png",
            contactPoint: [
              {
                "@type": "ContactPoint",
                telephone: "+91-9836114520",
                contactType: "sales",
                areaServed: "IN",
                availableLanguage: ["English", "Hindi"],
              },
              {
                "@type": "ContactPoint",
                telephone: "+91-7890090500",
                contactType: "customer service",
                areaServed: "IN",
                availableLanguage: ["English", "Hindi", "Bengali"],
              },
            ],
            address: {
              "@type": "PostalAddress",
              streetAddress: "FORMAX IT SOLUTIONS PVT. LTD.",
              addressLocality: "Kolkata",
              addressRegion: "West Bengal",
              addressCountry: "IN",
            },
          }),
        }}
      />

      <style>{`
        .card {
          transition: all 0.3s ease;
        }
        
        .card:hover {
          box-shadow: 0 5px 20px rgba(0,0,0,0.1) !important;
        }
        
        .form-control:focus {
          border-color: #4a6cf7;
          box-shadow: 0 0 0 0.2rem rgba(74, 108, 247, 0.25);
        }
        
        .fea {
          position: absolute;
          top: 50%;
          left: 15px;
          transform: translateY(-50%);
          z-index: 1;
        }
        
        .form-control.ps-5 {
          padding-left: 45px !important;
        }
        
        iframe {
          min-height: 500px;
          border-radius: 0.375rem;
        }
        
        /* Make all cards same height */
        .features.feature-clean {
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        
        .features.feature-clean .content {
          flex: 1;
        }
        
        .icons.text-center.mx-auto.mt-4 {
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .icons.text-center.mx-auto.mt-4 i {
          font-size: 2.5rem;
        }
      `}</style>

      <Footer />
    </>
  );
};

export default ContactUs;
